import { AUDIT_ACTION_LABELS } from '../../types/audit';
import type { AuditLogEntry } from '../../types/audit';
import {
  CONFLICT_SEVERITY_LABELS,
  CONFLICT_SEVERITY_TONES,
  SCHEDULE_CONFLICT_TYPE_LABELS,
  describeOverrideReason,
} from '../../types/assignment';

interface AuditLogTableProps {
  entries: readonly AuditLogEntry[];
  /** `User.id` → full name, so the ledger names people and not identifiers. */
  actorNames: Record<string, string>;
}

const stamp = (iso: string): string => {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  const pad = (part: number) => String(part).padStart(2, '0');
  return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
};

/**
 * The append-only ledger as a table: every manager override ("שבץ בכל זאת")
 * with the warning that was raised, the reason given, the manager who gave it
 * and the moment. Read-only by construction — nothing in the application edits
 * a row, and this screen offers no way to try.
 */
export function AuditLogTable({ entries, actorNames }: AuditLogTableProps) {
  const cellClass = 'border border-line align-top p-2 text-sm';
  const headClass = 'border border-line bg-canvas p-2 text-xs font-semibold text-ink/70 text-right';

  if (entries.length === 0) {
    return <p className="text-sm text-ink/60">אין רשומות ביומן לטווח שנבחר.</p>;
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full border-collapse min-w-[45rem]">
        <thead>
          <tr>
            <th className={headClass}>מועד</th>
            <th className={headClass}>פעולה</th>
            <th className={headClass}>הממצא שנעקף</th>
            <th className={headClass}>סיבת המנהל</th>
            <th className={headClass}>מנהל מאשר</th>
          </tr>
        </thead>
        <tbody>
          {entries.map((entry) => (
            <tr key={entry.id}>
              <td className={`${cellClass} whitespace-nowrap tabular-nums`} data-dir="ltr">
                {stamp(entry.createdAt)}
              </td>
              <td className={cellClass}>
                <p className="font-medium">{AUDIT_ACTION_LABELS[entry.action]}</p>
                {entry.override ? (
                  <span
                    className={[
                      'inline-block mt-1 rounded-lg border px-2 py-0.5 text-xs',
                      CONFLICT_SEVERITY_TONES[entry.override.severity],
                    ].join(' ')}
                  >
                    {CONFLICT_SEVERITY_LABELS[entry.override.severity]} ·{' '}
                    {SCHEDULE_CONFLICT_TYPE_LABELS[entry.override.conflictType]}
                  </span>
                ) : null}
              </td>
              <td className={cellClass}>{entry.override?.details ?? entry.summary}</td>
              <td className={cellClass}>
                {entry.override ? describeOverrideReason(entry.override) : '—'}
              </td>
              <td className={cellClass}>
                <p>{actorNames[entry.actorId] ?? entry.actorId}</p>
                <p className="text-xs text-ink/50" data-dir="ltr">
                  {entry.actorId}
                </p>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
